import axios from 'axios';
import { store } from './redux/configureStore';
import { PURGE } from "redux-persist";
import { setAuth } from './redux/reducers/utils/authReducer';
import { setCurrentServer } from './redux/reducers/utils/commonReducer';
import { message } from 'antd';
import CryptoJS from 'crypto-js';
import { captureApiError } from './hooks/useErrorTracking';
import { resolveCurrentServer } from './utils/serverConfig';
import { reportRequestSuccess, isLikelyNetworkError, reportRequestFailure } from './components/NetworkStatusLamp';

/*
 * HTTP-клиент приложения (axios) с дополнительной логикой:
 * - автоматическое определение baseURL в зависимости от выбранного сервера,
 * - подписывание тела запроса HMAC-SHA256 (generateSignature),
 * - защита от кэширования (заголовки + параметр _t для GET-запросов),
 * - централизованная обработка ошибок с передачей статуса сети в networkStatusService.
 */

// Таймаут ожидания синхронизации серверного времени перед подписанным запросом
const SERVER_TIME_BOOT_SYNC_TIMEOUT_MS = 8000;

// Интервал, в течение которого дублирующиеся тосты не будут показываться
const ERROR_TOAST_DEDUP_WINDOW_MS = 8000;
const errorToastTimestamps = new Map();

// Флаги и промисы для одноразовой синхронизации серверного времени при старте
let hasServerTimeBootSynced = false;
let serverTimeBootSyncPromise = null;

// Утилита ожидания
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/*
 * Показ сообщения об ошибке с дедупликацией по ключу.
 * Не позволяет спамить одинаковыми тостами подряд.
 */
const showErrorMessageWithDedup = (content, dedupKey = 'unknown-error') => {
  if (!content) return;

  const now = Date.now();
  const lastShownAt = errorToastTimestamps.get(dedupKey) || 0;

  if (now - lastShownAt < ERROR_TOAST_DEDUP_WINDOW_MS) {
    return;
  }

  errorToastTimestamps.set(dedupKey, now);

  // Периодически чистим карту дедупликации
  if (errorToastTimestamps.size > 200) {
    for (const [key, timestamp] of errorToastTimestamps.entries()) {
      if (now - timestamp > ERROR_TOAST_DEDUP_WINDOW_MS * 3) {
        errorToastTimestamps.delete(key);
      }
    }
  }

  message.error(content);
};

// Определяем, является ли request запросом к эндпойнту получения серверного времени
const isServerTimeRequest = (config) => {
  const rawUrl = config?.url;
  if (!rawUrl || typeof rawUrl !== 'string') {
    return false;
  }

  return rawUrl.includes('/getServerTime');
};

// Решаем, нужно ли ждать синхронизации серверного времени перед подписыванием запроса
const shouldWaitForServerTimeSync = (config) => {
  if (!config || !config.data) {
    return false;
  }

  if (isServerTimeRequest(config)) {
    return false;
  }

  return true;
};

// Диспатчит экшен получения серверного времени и бросает исключение при провале
const dispatchServerTimeSync = async () => {
  const { getServerTime } = require('./redux/reducers/utils/commonReducer');
  const result = await store.dispatch(getServerTime());

  if (result?.meta?.requestStatus !== 'fulfilled') {
    throw new Error('Failed to sync server time before signed request');
  }

  hasServerTimeBootSynced = true;
};

/*
 * Если перед запросом требуется актуальное серверное время (для корректной подписи),
 * пытаемся выполнить одноразовую синхронизацию и ждем либо результата, либо таймаута.
 */
const ensureFreshServerTimeBeforeSignedRequest = async (config) => {
  if (!shouldWaitForServerTimeSync(config) || hasServerTimeBootSynced) {
    return;
  }

  if (!serverTimeBootSyncPromise) {
    serverTimeBootSyncPromise = dispatchServerTimeSync()
      .catch((error) => {
        console.warn('Server time pre-sync failed, request will continue with current time.', error);
      })
      .finally(() => {
        serverTimeBootSyncPromise = null;
      });
  }

  await Promise.race([
    serverTimeBootSyncPromise,
    sleep(SERVER_TIME_BOOT_SYNC_TIMEOUT_MS),
  ]);
};

/*
 * Возвращает корректную метку времени (timestamp) для подписей и логов.
 * Использует клиентское время + known offset (timeDelta) если он есть.
 */
export const getServerTimestamp = (serverTime, clientTime, timeDelta) => {
  const safeClientTime = Number.isFinite(clientTime) ? clientTime : Date.now();
  const safeTimeDelta = Number.isFinite(timeDelta) ? timeDelta : 0;

  return safeClientTime + safeTimeDelta;
};

/*
 * Возвращает постоянный идентификатор машины, хранящийся в localStorage.
 * Если его нет — генерируем `crypto.randomUUID()` и сохраняем.
 */
export const getMachineId = () => {
  let machineId = localStorage.getItem('MachineId');

  if (!machineId) {
    machineId = crypto.randomUUID();
    localStorage.setItem('MachineId', machineId);
  }

  return machineId;
};

/*
 * Безопасно извлекает полезную информацию из body/данных запроса для логов.
 * Поддерживает FormData (с вытаскиванием имён файлов), объекты и JSON-строки.
 */
const getRequestData = (configData) => {
  if (!configData) return null;

  try {
    // Если это FormData
    if (configData instanceof FormData) {
      const formDataObj = {};
      for (const [key, value] of configData.entries()) {
        formDataObj[key] = value instanceof File ? `[File: ${value.name}]` : value;
      }
      return formDataObj;
    }

    // Если это уже объект
    if (typeof configData === 'object') {
      return configData;
    }

    // Если это строка JSON
    if (typeof configData === 'string') {
      return JSON.parse(configData);
    }

    return configData;
  } catch (e) {
    console.error('Failed to parse request data:', e);
    return { error: 'Failed to parse data', raw: String(configData) };
  }
};

/*
 * Генерирует HMAC-SHA256 подпись для данных запроса.
 * Алгоритм:
 *  - сериализуем данные (FormData -> простая структура),
 *  - формируем объект данных и вычисляем timestamp (серверное/локальное время с дельтой),
 *  - secretKey = timestamp + device + bearerToken,
 *  - signature = HmacSHA256(JSON.stringify(dataObj) + timestamp, secretKey).
 * Возвращаем подпись и отладочный объект для диагностики (не отправлять в прод).
 */
export const generateSignature = (data) => {
  let serializedData;

  if (data instanceof FormData) {
    serializedData = Array.from(data.entries()).reduce((acc, [key, value]) => {
      acc[key] = value instanceof File ? value.name : value; // Для файлов берем только имя
      return acc;
    }, {});
  } else {
    serializedData = data;
  }

  let dataObj
  if (serializedData.data) {
    dataObj = {data: JSON.parse(serializedData.data)};
  } else {
    dataObj = JSON.parse(serializedData);
  }

  const state = store.getState();
  const serverTime = state.common.serverTime; // "2026-03-04 07:07:14"
  const clientTime = Date.now();
  const timeDelta = state.common.timeDelta; // возможный сдвиг времени сервера
  const timestamp = getServerTimestamp(serverTime, clientTime, timeDelta).toString();
  const device = getMachineId();
  const token = localStorage.getItem('token') || '';
  const secretKey = timestamp + device + token;
  const message = (JSON.stringify(dataObj)) + timestamp;
  const signature = CryptoJS.HmacSHA256(message, secretKey).toString(CryptoJS.enc.Hex);

  return {
    signature,
    timestamp,
    device,
    debug: {
      serializedData,
      dataObj,
      message,
      secretKey,
      signature,
      token,
      timestamp,
      device,
      serverTime,
      clientTime,
      timeDriftMs: clientTime - Number(timestamp),
    },
  };
};

// Создаём инстанс axios с глобальным таймаутом
const apiInstance = axios.create({
  timeout: 30000, // 30 секунд таймаут для всех запросов
});

/*
 * Request interceptor:
 * - при необходимости синхронизирует серверное время,
 * - подставляет baseURL для API,
 * - добавляет Authorization header,
 * - запрещает кэширование на уровне браузера/прокси,
 * - добавляет _t для GET-запросов,
 * - и, если есть тело, добавляет подпись X-Signature.
 */
apiInstance.interceptors.request.use(
  async (config) => {
    await ensureFreshServerTimeBeforeSignedRequest(config);

    const storedCurrentServer = localStorage.getItem('currentServer');
    const currentServer = resolveCurrentServer(storedCurrentServer);

    if (storedCurrentServer !== currentServer) {
      localStorage.setItem('currentServer', currentServer);
    }

    const stateCurrentServer = store.getState().common?.currentServer;
    if (stateCurrentServer !== currentServer) {
      store.dispatch(setCurrentServer(currentServer));
    }

    const apiUrl = currentServer + '/api/v1/admin';

    config.baseURL = apiUrl;

    const token = localStorage.getItem('token');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Защита от кэширования: ставим строгие заголовки
    config.headers['Cache-Control'] = 'no-store, no-cache, must-revalidate, max-age=0';
    config.headers.Pragma = 'no-cache';
    config.headers.Expires = '0';

    const method = String(config.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head' || method === 'options') {
      // Добавляем параметр _t в GET-запросы, чтобы избежать кэширующих прокси
      config.params = {
        ...(config.params || {}),
        _t: Date.now(),
      };
    }
    
    if (config.data) { 
      const { signature, timestamp, device, debug } = generateSignature(config.data);
      config.headers['X-Signature'] = signature;
      config.headers['X-Timestamp'] = timestamp;
      config.headers['X-device'] = device;
      // Сохраняем отладочную информацию (не отправляем в сеть)
      config.__signatureDebug = debug;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/*
 * Response interceptor: помечаем успешные запросы и централизованно обрабатываем
 * ошибки - собираем метаданные, снимаем индикаторы сети и показываем пользователю
 * удобные сообщения в зависимости от HTTP-кода.
 */
apiInstance.interceptors.response.use(
  (response) => {
    // Отмечаем успешный запрос в сервисе сети
    reportRequestSuccess();
    return handleResponse(response);
  },
  async (error) => {
    // Проверяем, является ли ошибка сетевой
    if (isLikelyNetworkError(error)) {
      console.warn('🔴 Network error detected:', error.message, error.code);

      await reportRequestFailure({
        error,
        url: error.config?.url,
      });
    } else {
      // Если это не сетевая ошибка, считаем канал связи рабочим
      reportRequestSuccess();
    }
    
    return handleApiError(error);
  }
);

/*
 * Обрабатывает успешный ответ: если в payload есть человекочитаемое сообщение,
 * показываем его пользователю (success/error) в виде тоста.
 */
const handleResponse = (response) => {
  if (
    // Костыль для дефолтной роли. Плохо работает индикатор newUser от сервера. Старые роли без дефолтной роли. выдают newUser: false
    (response.data.data?.message && response.data.data?.message !== 'Дефолтная роль не создана') ||
    response.data?.message
  ) {
    response.data.result
    ? message.success(response.data.data?.message || response.data?.message)
    : message.error(response.data.data?.message || response.data?.message)
  }

  return response;
};

/*
 * Централизованная обработка ошибок API:
 * - собираем дополнительную телеметрию через `captureApiError`,
 * - показываем пользователю понятные сообщения в зависимости от HTTP-кода,
 * - для 401 очищаем токены и делаем лог-аут.
 */
const handleApiError = (error) => {
  if (error.response) {
    const state = store.getState();
    const fallbackUserTimestamp = getServerTimestamp(
      state.common?.serverTime,
      Date.now(),
      state.common?.timeDelta
    ).toString();
    const requestTimestamp = error.config?.headers?.['X-Timestamp'];

    captureApiError(error, {
      requestUrl: error.config?.url,
      requestMethod: error.config?.method?.toUpperCase(),
      errorCode: error.response.status,
      errorMessage: error.response.data?.message || 'Нет сообщения об ошибке',
      requestData: getRequestData(error.config?.data),
      responseData: error.response?.data,
      userPhone: store.getState().auth.userProfile?.phone || 'unknown',
      userTime: requestTimestamp || fallbackUserTimestamp,
      token: localStorage.getItem('token'),
      signature: error.config?.headers?.['X-Signature'],
      signatureDebug: error.config?.__signatureDebug,
      kitchenId: store.getState().kitchen.selectKitchen?.id,
      kitchenName: store.getState().kitchen.selectKitchen?.name,
      browser: navigator.userAgent,
      pageUrl: window.location.href,
    });

    switch (error.response.status) {
      case 400:
        console.log('Некорректные данные!');
        // Если есть нормальная ошибка в виде data.message, показываем её
        const errData = error.response.data;
        if (error.response.data.data?.message || error.response.data.message) {
          showErrorMessageWithDedup(
            `Некорректные данные! проверьте правильность вводимых данных. (код ошибки 400). ${`Причина: ${error.response.data.data?.message || error.response.data.message}`}`,
            `http-400:${error.response.data.data?.message || error.response.data.message || 'unknown'}`
          );
        } else {
          // Обработка ошибок в виде объекта с массивами
          let errorText = '';
          if (errData && typeof errData === 'object') {
            errorText = Object.entries(errData)
              .map(([key, value]) => {
                if (Array.isArray(value)) {
                  return value.map(v => `${key}: ${v}`).join('\n');
                } else if (typeof value === 'string') {
                  return `${key}: ${value}`;
                } else {
                  return '';
                }
              })
              .filter(Boolean)
              .join('\n');
          }
          showErrorMessageWithDedup(
            `Некорректные данные! Проверьте правильность вводимых данных. (код ошибки 400)${errorText ? '\n' + errorText : ''}`,
            `http-400:${errorText || 'validation-object'}`
          );
        }
        break;
      case 401:
        console.log('Не авторизован!');
        const token = localStorage.getItem('token');
        if (token) {
          // Если токен есть, но получили 401, значит токен недействителен
          showErrorMessageWithDedup(
            `Ваш токен: ${token} недействителен. Пожалуйста, войдите в систему снова. (код ошибки 401)`,
            'http-401:token-invalid'
          );
        } else {
          // Если токена нет, просто сообщаем о необходимости авторизации
          showErrorMessageWithDedup(
            'Не авторизован! Пожалуйста, войдите в систему снова. (код ошибки 401)',
            'http-401:no-token'
          );
        }

        // Обновляем состояние авторизации в redux (отключаем доступ)
        try { store.dispatch(setAuth(false)); } catch (e) { /* noop */ }

        // Очищаем локальное хранилище
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('session');
        } catch (e) { /* noop */ }

        // Просим redux-persist очистить хранилище (если используется)
        try { store.dispatch({ type: PURGE }); } catch (e) { /* noop */ }

        break;
      case 403:
        console.log('Недостаточно прав!');
        showErrorMessageWithDedup(
          error.response.data.data?.message || error.response.data.message || 'У Вас недостаточно прав для выполнения этого действия! (код ошибки 403)',
          `http-403:${error.response.data.data?.message || error.response.data.message || 'forbidden'}`
        );
        break;
      case 404:
        console.log('Api не найден!');
        showErrorMessageWithDedup(
          error.response.data.data?.message || error.response.data.message || 'Api не найден! (код ошибки 404)',
          `http-404:${error.response.data.data?.message || error.response.data.message || 'not-found'}`
        );
        break;
      case 422:
        console.log('Некорректные данные!');
        showErrorMessageWithDedup(
          error.response.data.data?.message || error.response.data.message || 'Некорректные данные! проверьте правильность вводимых данных. (код ошибки 422)',
          `http-422:${error.response.data.data?.message || error.response.data.message || 'validation'}`
        );
        break;
      case 500:
        console.log('Проблемы на сервере!');
        showErrorMessageWithDedup(
          error.response.data.data?.message || error.response.data.message || 'Проблемы на сервере! (код ошибки 500)',
          `http-500:${error.response.data.data?.message || error.response.data.message || 'server-error'}`
        );
        break;
      default:
        console.log('Не обрабатываемый код ошибки!');
        showErrorMessageWithDedup(
          error.response.data.data?.message || error.response.data.message || 'Не обрабатываемый код ошибки!',
          `http-${error.response.status || 'unknown'}:${error.response.data.data?.message || error.response.data.message || 'unhandled'}`
        )
        break;
    }
  } else if (isLikelyNetworkError(error)) {
    captureApiError(error, {
      requestUrl: error.config?.url,
      requestMethod: error.config?.method?.toUpperCase(),
      requestData: getRequestData(error.config?.data),
      browser: navigator.userAgent,
      pageUrl: window.location.href,
      networkOnly: true,
    });
    // Различаем типы сетевых ошибок для более точного сообщения
    if (error.code === 'ETIMEDOUT' || error.message?.toLowerCase().includes('timeout')) {
      console.log('🔴 Request timeout:', error.message);
      showErrorMessageWithDedup('Превышено время ожидания ответа от сервера!', 'network:timeout');
    } else if (error.code === 'ERR_CONNECTION_REFUSED') {
      console.log('🔴 Connection refused:', error.message);
      showErrorMessageWithDedup('Сервер отклонил подключение!', 'network:connection-refused');
    } else {
      console.log('🔴 Network connection error:', error.message);
      showErrorMessageWithDedup('Не получается получить ответ от сервера! Проверьте интернет соединение', 'network:generic');
    }
  } else {
    console.log(error);
    showErrorMessageWithDedup(
      'По каким-то причинам запрос не был выполнен и не был получен ответ от сервера!',
      'network:unknown'
    );
  }
  return Promise.reject(error);
};

/*
 * Обёртка для GraphQL запросов: формирует endpoint на основе текущего сервера
 * и делает POST-запрос через axios. В случае ошибки бросает универсальную ошибку.
 */
export const fetchGraphQL = async (query) => {
  try {
    const currentServer = resolveCurrentServer(localStorage.getItem('currentServer'));
    const graphUrl = currentServer + '/api/graphql/5496880-8345687-2394984-221781/admin';

    const response = await axios.post(
      graphUrl,
      { query },
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error);
    throw new Error('Ошибка при выполнении запроса');
  }
};

export default apiInstance;
